/**
 * The workspace roles allowed to perform writes guarded by `withWriter`
 * (superadmin | admin | editor | accountant).
 *
 * One list for every surface that performs the same write - the HTTP guard,
 * an AI chat tool, and the client deciding which controls to offer - so they
 * can never disagree (OCT-15906).
 */
export const WRITER_ROLES = ["superadmin", "admin", "editor", "accountant"] as const;

/** True when `role` is one `withWriter` admits. */
export const isWriterRole = (role: string | null | undefined): boolean =>
  !!role && (WRITER_ROLES as readonly string[]).includes(role);

/** The read-only workspace role. */
export const VIEWER_ROLE = "viewer" as const;

/**
 * True only for the Viewer role (OCT-17302).
 *
 * The product-wide "may not change workspace data" rule targets the Viewer
 * role explicitly. It is never "not in the writer list": the legacy `member`
 * role, an unknown role and a missing role are NOT viewers, so they keep
 * exactly the access they had before OCT-16925. The central HTTP check, the
 * AI chat tool runner and the client's `useCanWrite()` all read this one
 * predicate so they can never disagree.
 *
 * @spec ILLEGAL E157
 */
export const isViewerRole = (role: string | null | undefined): boolean => role === VIEWER_ROLE;
